import React, { useState, useEffect, createContext, useContext, useMemo, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import AdminDashboard from "./AdminDashboard";
import Portal from "./Portal";
import { 
  Globe, 
  ShoppingCart, 
  Package, 
  Truck, 
  Palette, 
  Cpu,
  ArrowRight,
  ArrowLeft,
  Mail,
  Instagram,
  Linkedin,
  Menu,
  X,
  Check,
  Settings,
  MapPin,
  Clock,
  Briefcase,
  Star,
  Send,
  User,
  Twitter,
  Facebook,
  Youtube,
  AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext();

const useLanguage = () => useContext(LanguageContext);

// Translations
const translations = {
  en: {
    dir: "ltr",
    nav: {
      about: "About",
      services: "Services",
      portfolio: "Portfolio",
      contact: "Contact",
      brand: "Brand"
    },
    hero: {
      tagline: "DIGITAL INNOVATION • CREATIVE SOLUTIONS • GLOBAL REACH",
      headline: "Building brands that capture attention and last.",
      cta: "Discover Our Projects"
    },
    about: {
      title: "OUR MISSION",
      description: "Mahzod Media Group develops and operates digital products, e-commerce projects, and physical brand solutions – all under one roof. Our goal is to create products and brands that are globally recognized, capture attention, and have lasting impact."
    },
    services: {
      title: "SERVICES",
      items: [
        { title: "E-Commerce & Digital Products", desc: "End-to-end digital solutions for modern businesses" },
        { title: "Physical Products & Brands", desc: "Tangible brand experiences that resonate" },
        { title: "Import & Export", desc: "Global trade networks and logistics expertise" },
        { title: "Branding & Marketing", desc: "Strategic brand positioning and market presence" },
        { title: "Technology & Innovation", desc: "Cutting-edge tech solutions for tomorrow" },
        { title: "Custom Solutions", desc: "Tailored strategies and solutions for your unique needs" }
      ]
    },
    portfolio: {
      title: "PORTFOLIO",
      description: "All projects, whether digital solutions, e-commerce platforms, or physical products, operate under Mahzod Media Group and benefit from central expertise, global networks, and strategic know-how.",
      projects: [
        { title: "Digital Commerce", category: "E-Commerce" },
        { title: "Brand Identity", category: "Branding" },
        { title: "Global Logistics", category: "Import/Export" },
        { title: "Tech Solutions", category: "Innovation" }
      ]
    },
    contact: {
      title: "CONTACT US",
      subtitle: "For Digital & Physical Solutions",
      required: "* indicates required",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      phone: "Phone Number",
      company: "Company",
      city: "City",
      country: "Country",
      message: "Message",
      send: "Send Message",
      captcha: "Security Question",
      captchaError: "Incorrect answer. Please try again.",
      success: "Message sent successfully!",
      error: "Failed to send message. Please try again."
    },
    footer: {
      copyright: "Mahzod Media Group. All rights reserved.",
      tagline: "Building brands that last.",
      links: {
        brand: "Brand Guidelines",
        imprint: "Imprint",
        privacy: "Privacy Policy",
        jobs: "Jobs"
      }
    },
    imprint: {
      title: "IMPRINT",
      company: "Mahzod Media Group",
      address: "Hügelstraße 32",
      city: "49497 Mettingen",
      country: "Germany",
      taxNumber: "Tax Number",
      vatId: "VAT ID",
      management: "Management",
      contact: "Contact",
      email: "Email",
      disclaimer: {
        title: "Disclaimer",
        liability: {
          title: "Liability for Content",
          text: "The contents of our pages were created with the greatest care. However, we cannot guarantee the accuracy, completeness, and timeliness of the content. As a service provider, we are responsible for our own content on these pages according to general laws. However, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity."
        },
        links: {
          title: "Liability for Links",
          text: "Our offer contains links to external websites of third parties, on whose contents we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the contents of the linked pages."
        },
        copyright: {
          title: "Copyright",
          text: "The content and works on these pages created by the site operators are subject to German copyright law. Duplication, processing, distribution, and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator."
        }
      }
    },
    privacy: {
      title: "PRIVACY POLICY",
      intro: "We take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with the statutory data protection regulations and this privacy policy.",
      sections: {
        responsible: {
          title: "1. Responsible Party",
          text: "The responsible party for data processing on this website is:"
        },
        dataCollection: {
          title: "2. Data Collection on Our Website",
          cookies: {
            title: "Cookies",
            text: "Our website uses cookies. These are small text files that your web browser stores on your device. Cookies help us make our website more user-friendly, effective, and secure. Some cookies are 'session cookies.' Such cookies are automatically deleted after your visit. Other cookies remain on your device until you delete them. These cookies allow us to recognize your browser on your next visit."
          },
          server: {
            title: "Server Log Files",
            text: "The website provider automatically collects and stores information in so-called server log files, which your browser automatically transmits to us. These are: browser type and version, operating system used, referrer URL, hostname of the accessing computer, time of the server request, and IP address."
          }
        },
        rights: {
          title: "3. Your Rights",
          text: "You have the right to request information about your stored personal data, its origin, recipients, and the purpose of data processing free of charge at any time. You also have the right to request the correction, blocking, or deletion of this data. For this purpose, as well as for further questions regarding data protection, you can contact us at any time at the address given in the imprint."
        },
        contact: {
          title: "4. Contact Form",
          text: "If you send us inquiries via the contact form, your details from the inquiry form, including the contact data you provide there, will be stored by us for the purpose of processing the inquiry and in case of follow-up questions."
        }
      }
    },
    jobs: {
      title: "JOBS",
      subtitle: "Join Our Team",
      currentPositions: "CURRENTLY OPEN POSITIONS",
      createAlert: "Create a Job Alert",
      createAlertPlaceholder: "Enter your email",
      createAlertButton: "Create alert",
      featuredJobs: "Featured Jobs",
      search: "Search",
      searchPlaceholder: "Search jobs...",
      office: "Office",
      selectOffice: "Select...",
      function: "Function",
      selectFunction: "Select...",
      jobsAvailable: "jobs available",
      noJobs: "No jobs match your criteria",
      applyNow: "Apply Now",
      location: "Location",
      type: "Type",
      talentText: "Don't see a position that fits? We're always looking for exceptional talent.",
      sendApplication: "Send us your application:",
      email: "info@mahzod.com",
      offices: ["All Offices", "Mettingen", "Remote"],
      functions: ["All Functions", "Marketing", "Technology", "Design", "Sales", "Operations"],
      backToJobs: "Back to jobs",
      featured: "Featured",
      whatYoullDo: "What you'll do:",
      whatYouNeed: "What you need:",
      applyForJob: "Apply for this job",
      firstName: "First Name",
      lastName: "Last Name",
      emailField: "Email",
      phone: "Phone",
      coverLetter: "Cover Letter",
      resumeUrl: "Resume/CV URL or LinkedIn",
      submit: "Submit Application",
      applicationSuccess: "Application submitted successfully!",
      applicationError: "Failed to submit application",
      requiredField: "indicates a required field"
    },
    brand: {
      title: "BRAND GUIDELINES",
      subtitle: "Our visual identity standards ensure consistent and professional brand representation across all platforms.",
      colors: {
        title: "COLOR PALETTE",
        primary: "Primary Colors",
        secondary: "Secondary Colors"
      },
      typography: {
        title: "TYPOGRAPHY",
        headings: "Headings",
        body: "Body Text",
        sample: "The quick brown fox jumps over the lazy dog."
      },
      logo: {
        title: "LOGO USAGE",
        versions: "Logo Versions",
        primary: "Primary (Dark)",
        inverted: "Inverted (Light)",
        clearSpace: "Clear Space",
        clearSpaceDesc: "Always maintain minimum clear space around the logo equal to the height of the 'M' in MAHZOD.",
        minSize: "Minimum Size",
        minSizeDesc: "The logo should never be smaller than 80px in width for digital applications."
      },
      dos: {
        title: "DO'S",
        items: [
          "Use the logo on solid black or white backgrounds",
          "Maintain the aspect ratio when scaling",
          "Use approved color combinations only",
          "Ensure sufficient contrast for readability",
          "Use Barlow Condensed for headlines",
          "Keep generous whitespace in layouts"
        ]
      },
      donts: {
        title: "DON'TS",
        items: [
          "Don't place the logo on busy backgrounds",
          "Don't stretch or distort the logo",
          "Don't use gradients on the logo",
          "Don't use rounded corners on brand elements",
          "Don't use drop shadows",
          "Don't change the logo colors",
          "Don't use the logo smaller than minimum size",
          "Don't rotate or tilt the logo"
        ]
      },
      download: "Download Brand Assets"
    }
  },
  de: {
    dir: "ltr",
    nav: {
      about: "Über uns",
      services: "Leistungen",
      portfolio: "Projekte",
      contact: "Kontakt",
      brand: "Marke"
    },
    hero: {
      tagline: "DIGITALE INNOVATION • KREATIVE LÖSUNGEN • WELTWEITE REICHWEITE",
      headline: "Marken aufbauen, die Aufmerksamkeit erregen und bestehen.",
      cta: "Entdecken Sie unsere Projekte"
    },
    about: {
      title: "UNSERE MISSION",
      description: "Mahzod Media Group entwickelt und betreibt digitale Produkte, E-Commerce-Projekte und physische Markenlösungen – alles unter einem Dach. Unser Ziel ist es, Marken und Produkte zu schaffen, die weltweit wirken, Aufmerksamkeit erregen und langfristig bestehen."
    },
    services: {
      title: "LEISTUNGEN",
      items: [
        { title: "E-Commerce & Digitale Produkte", desc: "Ganzheitliche digitale Lösungen für moderne Unternehmen" },
        { title: "Physische Produkte & Marken", desc: "Greifbare Markenerlebnisse, die berühren" },
        { title: "Import & Export", desc: "Globale Handelsnetzwerke und Logistik-Expertise" },
        { title: "Branding & Marketing", desc: "Strategische Markenpositionierung und Marktpräsenz" },
        { title: "Technologie & Innovation", desc: "Zukunftsweisende Tech-Lösungen" },
        { title: "Maßgeschneiderte Lösungen", desc: "Individuelle Strategien und Lösungen für Ihre einzigartigen Anforderungen" }
      ]
    },
    portfolio: {
      title: "PROJEKTE",
      description: "Alle Projekte – digitale Lösungen, E-Commerce-Shops oder physische Produkte – laufen unter dem Dach von Mahzod Media Group und profitieren von zentraler Expertise, globalen Netzwerken und strategischem Know-how.",
      projects: [
        { title: "Digitaler Handel", category: "E-Commerce" },
        { title: "Markenidentität", category: "Branding" },
        { title: "Globale Logistik", category: "Import/Export" },
        { title: "Tech-Lösungen", category: "Innovation" }
      ]
    },
    contact: {
      title: "KONTAKTIEREN SIE UNS",
      subtitle: "Für digitale & physische Lösungen",
      required: "* Pflichtfeld",
      firstName: "Vorname",
      lastName: "Nachname",
      email: "E-Mail",
      phone: "Telefonnummer",
      company: "Unternehmen",
      city: "Stadt",
      country: "Land",
      message: "Nachricht",
      send: "Nachricht senden",
      captcha: "Sicherheitsfrage",
      captchaError: "Falsche Antwort. Bitte versuchen Sie es erneut.",
      success: "Nachricht erfolgreich gesendet!",
      error: "Fehler beim Senden. Bitte versuchen Sie es erneut."
    },
    footer: {
      copyright: "Mahzod Media Group. Alle Rechte vorbehalten.",
      tagline: "Marken aufbauen, die bestehen.",
      links: {
        brand: "Markenrichtlinien",
        imprint: "Impressum",
        privacy: "Datenschutz",
        jobs: "Jobs"
      }
    },
    imprint: {
      title: "IMPRESSUM",
      company: "Mahzod Media Group",
      address: "Hügelstraße 32",
      city: "49497 Mettingen",
      country: "Deutschland",
      taxNumber: "Steuernummer",
      vatId: "USt-IdNr.",
      management: "Geschäftsführung",
      contact: "Kontakt",
      email: "E-Mail",
      disclaimer: {
        title: "Haftungsausschluss",
        liability: {
          title: "Haftung für Inhalte",
          text: "Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen."
        },
        links: {
          title: "Haftung für Links",
          text: "Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich."
        },
        copyright: {
          title: "Urheberrecht",
          text: "Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers."
        }
      }
    },
    privacy: {
      title: "DATENSCHUTZERKLÄRUNG",
      intro: "Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.",
      sections: {
        responsible: {
          title: "1. Verantwortliche Stelle",
          text: "Verantwortlich für die Datenverarbeitung auf dieser Website ist:"
        },
        dataCollection: {
          title: "2. Datenerfassung auf unserer Website",
          cookies: {
            title: "Cookies",
            text: "Unsere Website verwendet Cookies. Das sind kleine Textdateien, die Ihr Webbrowser auf Ihrem Endgerät speichert. Cookies helfen uns dabei, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen. Einige Cookies sind 'Session-Cookies'. Solche Cookies werden nach Ende Ihres Besuchs automatisch gelöscht. Andere Cookies bleiben auf Ihrem Endgerät gespeichert, bis Sie diese löschen. Diese Cookies ermöglichen es uns, Ihren Browser beim nächsten Besuch wiederzuerkennen."
          },
          server: {
            title: "Server-Log-Dateien",
            text: "Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind: Browsertyp und Browserversion, verwendetes Betriebssystem, Referrer URL, Hostname des zugreifenden Rechners, Uhrzeit der Serveranfrage und IP-Adresse."
          }
        },
        rights: {
          title: "3. Ihre Rechte",
          text: "Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Ihre gespeicherten personenbezogenen Daten, deren Herkunft und Empfänger und den Zweck der Datenverarbeitung. Sie haben außerdem ein Recht auf Berichtigung, Sperrung oder Löschung dieser Daten. Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit unter der im Impressum angegebenen Adresse an uns wenden."
        },
        contact: {
          title: "4. Kontaktformular",
          text: "Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert."
        }
      }
    },
    jobs: {
      title: "JOBS",
      subtitle: "Werde Teil unseres Teams",
      currentPositions: "AKTUELL OFFENE STELLEN",
      createAlert: "Job-Benachrichtigung erstellen",
      createAlertPlaceholder: "E-Mail eingeben",
      createAlertButton: "Benachrichtigung erstellen",
      featuredJobs: "Ausgewählte Stellen",
      search: "Suche",
      searchPlaceholder: "Jobs suchen...",
      office: "Standort",
      selectOffice: "Auswählen...",
      function: "Bereich",
      selectFunction: "Auswählen...",
      jobsAvailable: "Stellen verfügbar",
      noJobs: "Keine Jobs entsprechen Ihren Kriterien",
      applyNow: "Jetzt bewerben",
      location: "Standort",
      type: "Art",
      talentText: "Keine passende Stelle gefunden? Wir suchen immer nach außergewöhnlichen Talenten.",
      sendApplication: "Sende uns deine Bewerbung:",
      email: "info@mahzod.com",
      offices: ["Alle Standorte", "Mettingen", "Remote"],
      functions: ["Alle Bereiche", "Marketing", "Technologie", "Design", "Vertrieb", "Operations"],
      backToJobs: "Zurück zu Jobs",
      featured: "Empfohlen",
      whatYoullDo: "Deine Aufgaben:",
      whatYouNeed: "Was du mitbringst:",
      applyForJob: "Jetzt bewerben",
      firstName: "Vorname",
      lastName: "Nachname",
      emailField: "E-Mail",
      phone: "Telefon",
      coverLetter: "Anschreiben",
      resumeUrl: "Lebenslauf-URL oder LinkedIn",
      submit: "Bewerbung absenden",
      applicationSuccess: "Bewerbung erfolgreich eingereicht!",
      applicationError: "Bewerbung konnte nicht gesendet werden",
      requiredField: "Pflichtfeld"
    },
    brand: {
      title: "MARKENRICHTLINIEN",
      subtitle: "Unsere visuellen Identitätsstandards gewährleisten eine konsistente und professionelle Markendarstellung auf allen Plattformen.",
      colors: {
        title: "FARBPALETTE",
        primary: "Primärfarben",
        secondary: "Sekundärfarben"
      },
      typography: {
        title: "TYPOGRAFIE",
        headings: "Überschriften",
        body: "Fließtext",
        sample: "Franz jagt im komplett verwahrlosten Taxi quer durch Bayern."
      },
      logo: {
        title: "LOGO-VERWENDUNG",
        versions: "Logo-Versionen",
        primary: "Primär (Dunkel)",
        inverted: "Invertiert (Hell)",
        clearSpace: "Schutzraum",
        clearSpaceDesc: "Halten Sie immer einen Mindestabstand um das Logo ein, der der Höhe des 'M' in MAHZOD entspricht.",
        minSize: "Mindestgröße",
        minSizeDesc: "Das Logo sollte für digitale Anwendungen nie kleiner als 80px in der Breite sein."
      },
      dos: {
        title: "RICHTIG",
        items: [
          "Logo auf einfarbigem schwarzen oder weißen Hintergrund verwenden",
          "Seitenverhältnis beim Skalieren beibehalten",
          "Nur genehmigte Farbkombinationen verwenden",
          "Ausreichenden Kontrast für Lesbarkeit sicherstellen",
          "Barlow Condensed für Überschriften verwenden",
          "Großzügigen Weißraum in Layouts beibehalten"
        ]
      },
      donts: {
        title: "FALSCH",
        items: [
          "Logo nicht auf unruhigen Hintergründen platzieren",
          "Logo nicht strecken oder verzerren",
          "Keine Verläufe auf dem Logo verwenden",
          "Keine abgerundeten Ecken bei Markenelementen",
          "Keine Schlagschatten verwenden",
          "Logofarben nicht ändern",
          "Logo nicht kleiner als Mindestgröße verwenden",
          "Logo nicht drehen oder kippen"
        ]
      },
      download: "Marken-Assets herunterladen"
    }
  },
  ar: {
    dir: "rtl",
    nav: {
      about: "من نحن",
      services: "الخدمات",
      portfolio: "المشاريع",
      contact: "اتصل بنا",
      brand: "الهوية"
    },
    hero: {
      tagline: "ابتكار رقمي • حلول إبداعية • انتشار عالمي",
      headline: "بناء علامات تجارية تجذب الانتباه وتدوم.",
      cta: "اكتشف مشاريعنا"
    },
    about: {
      title: "مهمتنا",
      description: "تقوم مجموعة محظوظ ميديا بتطوير وتشغيل المنتجات الرقمية ومشاريع التجارة الإلكترونية وحلول العلامات التجارية المادية – كل ذلك تحت سقف واحد. هدفنا هو إنشاء منتجات وعلامات تجارية معترف بها عالمياً، تجذب الانتباه ولها تأثير دائم."
    },
    services: {
      title: "الخدمات",
      items: [
        { title: "التجارة الإلكترونية والمنتجات الرقمية", desc: "حلول رقمية شاملة للأعمال الحديثة" },
        { title: "المنتجات والعلامات التجارية المادية", desc: "تجارب علامات تجارية ملموسة ومؤثرة" },
        { title: "الاستيراد والتصدير", desc: "شبكات تجارية عالمية وخبرة لوجستية" },
        { title: "العلامات التجارية والتسويق", desc: "تموضع استراتيجي للعلامة التجارية وحضور سوقي" },
        { title: "التكنولوجيا والابتكار", desc: "حلول تقنية متطورة للمستقبل" },
        { title: "حلول مخصصة", desc: "استراتيجيات وحلول مصممة خصيصاً لاحتياجاتك الفريدة" }
      ]
    },
    portfolio: {
      title: "المشاريع",
      description: "جميع المشاريع، سواء كانت حلولاً رقمية أو منصات تجارة إلكترونية أو منتجات مادية، تعمل تحت مظلة مجموعة محظوظ ميديا وتستفيد من الخبرة المركزية والشبكات العالمية والمعرفة الاستراتيجية.",
      projects: [
        { title: "التجارة الرقمية", category: "التجارة الإلكترونية" },
        { title: "هوية العلامة التجارية", category: "العلامات التجارية" },
        { title: "اللوجستيات العالمية", category: "استيراد/تصدير" },
        { title: "الحلول التقنية", category: "الابتكار" }
      ]
    },
    contact: {
      title: "اتصل بنا",
      subtitle: "للحلول الرقمية والمادية",
      required: "* حقل مطلوب",
      firstName: "الاسم الأول",
      lastName: "اسم العائلة",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
      company: "الشركة",
      city: "المدينة",
      country: "الدولة",
      message: "الرسالة",
      send: "إرسال الرسالة",
      captcha: "سؤال الأمان",
      captchaError: "إجابة خاطئة. يرجى المحاولة مرة أخرى.",
      success: "تم إرسال الرسالة بنجاح!",
      error: "فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى."
    },
    footer: {
      copyright: "مجموعة محظوظ ميديا. جميع الحقوق محفوظة.",
      tagline: "بناء علامات تجارية تدوم.",
      links: {
        brand: "إرشادات الهوية",
        imprint: "البيانات القانونية",
        privacy: "سياسة الخصوصية",
        jobs: "الوظائف"
      }
    },
    imprint: {
      title: "البيانات القانونية",
      company: "Mahzod Media Group",
      address: "Hügelstraße 32",
      city: "49497 Mettingen",
      country: "ألمانيا",
      taxNumber: "الرقم الضريبي",
      vatId: "رقم ضريبة القيمة المضافة",
      management: "الإدارة",
      contact: "اتصل",
      email: "البريد الإلكتروني",
      disclaimer: {
        title: "إخلاء المسؤولية",
        liability: {
          title: "المسؤولية عن المحتوى",
          text: "تم إنشاء محتويات صفحاتنا بأقصى قدر من العناية. ومع ذلك، لا يمكننا ضمان دقة المحتوى واكتماله وحداثته. بصفتنا مزود خدمة، نحن مسؤولون عن المحتوى الخاص بنا على هذه الصفحات وفقًا للقوانين العامة."
        },
        links: {
          title: "المسؤولية عن الروابط",
          text: "يحتوي عرضنا على روابط لمواقع خارجية لأطراف ثالثة، والتي ليس لنا أي تأثير على محتواها. لذلك، لا يمكننا تحمل أي مسؤولية عن هذه المحتويات الخارجية."
        },
        copyright: {
          title: "حقوق النشر",
          text: "المحتوى والأعمال على هذه الصفحات التي أنشأها مشغلو الموقع تخضع لقانون حقوق النشر الألماني. يتطلب النسخ والمعالجة والتوزيع وأي نوع من الاستغلال موافقة كتابية من المؤلف أو المنشئ المعني."
        }
      }
    },
    privacy: {
      title: "سياسة الخصوصية",
      intro: "نحن نأخذ حماية بياناتك الشخصية على محمل الجد. نتعامل مع بياناتك الشخصية بسرية ووفقًا للوائح حماية البيانات القانونية وسياسة الخصوصية هذه.",
      sections: {
        responsible: {
          title: "1. الجهة المسؤولة",
          text: "الجهة المسؤولة عن معالجة البيانات على هذا الموقع هي:"
        },
        dataCollection: {
          title: "2. جمع البيانات على موقعنا",
          cookies: {
            title: "ملفات تعريف الارتباط",
            text: "يستخدم موقعنا ملفات تعريف الارتباط. هذه ملفات نصية صغيرة يخزنها متصفح الويب الخاص بك على جهازك. تساعدنا ملفات تعريف الارتباط في جعل موقعنا أكثر سهولة وفعالية وأمانًا."
          },
          server: {
            title: "ملفات سجل الخادم",
            text: "يقوم مزود الموقع بجمع وتخزين المعلومات تلقائيًا في ملفات سجل الخادم، والتي يرسلها متصفحك إلينا تلقائيًا."
          }
        },
        rights: {
          title: "3. حقوقك",
          text: "لديك الحق في طلب معلومات حول بياناتك الشخصية المخزنة ومصدرها والمستلمين والغرض من معالجة البيانات مجانًا في أي وقت. لديك أيضًا الحق في طلب تصحيح أو حظر أو حذف هذه البيانات."
        },
        contact: {
          title: "4. نموذج الاتصال",
          text: "إذا أرسلت إلينا استفسارات عبر نموذج الاتصال، فسيتم تخزين تفاصيلك من نموذج الاستفسار لدينا لغرض معالجة الاستفسار."
        }
      }
    },
    jobs: {
      title: "الوظائف",
      subtitle: "انضم إلى فريقنا",
      currentPositions: "الوظائف المتاحة حالياً",
      createAlert: "إنشاء تنبيه وظيفي",
      createAlertPlaceholder: "أدخل بريدك الإلكتروني",
      createAlertButton: "إنشاء تنبيه",
      featuredJobs: "الوظائف المميزة",
      search: "بحث",
      searchPlaceholder: "البحث عن وظائف...",
      office: "المكتب",
      selectOffice: "اختر...",
      function: "القسم",
      selectFunction: "اختر...",
      jobsAvailable: "وظيفة متاحة",
      noJobs: "لا توجد وظائف تطابق معاييرك",
      applyNow: "قدم الآن",
      location: "الموقع",
      type: "النوع",
      talentText: "لم تجد وظيفة مناسبة؟ نحن نبحث دائماً عن المواهب الاستثنائية.",
      sendApplication: "أرسل لنا طلبك:",
      email: "info@mahzod.com",
      offices: ["جميع المكاتب", "ميتينغن", "عن بُعد"],
      functions: ["جميع الأقسام", "التسويق", "التكنولوجيا", "التصميم", "المبيعات", "العمليات"]
    },
    brand: {
      title: "إرشادات الهوية",
      subtitle: "تضمن معايير هويتنا البصرية تمثيلاً متسقاً واحترافياً للعلامة التجارية عبر جميع المنصات.",
      colors: {
        title: "لوحة الألوان",
        primary: "الألوان الأساسية",
        secondary: "الألوان الثانوية"
      },
      typography: {
        title: "الطباعة",
        headings: "العناوين",
        body: "النص الأساسي",
        sample: "نص عربي تجريبي لعرض الخط المستخدم في التصميم."
      },
      logo: {
        title: "استخدام الشعار",
        versions: "إصدارات الشعار",
        primary: "أساسي (داكن)",
        inverted: "معكوس (فاتح)",
        clearSpace: "المساحة الآمنة",
        clearSpaceDesc: "حافظ دائماً على مساحة آمنة حول الشعار تساوي ارتفاع حرف 'M' في MAHZOD.",
        minSize: "الحجم الأدنى",
        minSizeDesc: "يجب ألا يكون الشعار أصغر من 80 بكسل في العرض للتطبيقات الرقمية."
      },
      dos: {
        title: "افعل",
        items: [
          "استخدم الشعار على خلفيات سوداء أو بيضاء صلبة",
          "حافظ على نسبة العرض إلى الارتفاع عند التكبير",
          "استخدم مجموعات الألوان المعتمدة فقط",
          "تأكد من التباين الكافي للقراءة",
          "استخدم Barlow Condensed للعناوين",
          "حافظ على مساحة بيضاء سخية في التصميمات"
        ]
      },
      donts: {
        title: "لا تفعل",
        items: [
          "لا تضع الشعار على خلفيات مشغولة",
          "لا تمدد أو تشوه الشعار",
          "لا تستخدم التدرجات على الشعار",
          "لا تستخدم زوايا دائرية على عناصر العلامة",
          "لا تستخدم الظلال",
          "لا تغير ألوان الشعار",
          "لا تستخدم الشعار أصغر من الحجم الأدنى",
          "لا تقم بتدوير أو إمالة الشعار"
        ]
      },
      download: "تحميل أصول العلامة التجارية"
    }
  }
};

// Navbar Component
const Navbar = () => {
  const { language, setLanguage, t } = useLanguage();
  const sc = useSiteContent();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = sc?.navigation?.items?.filter(i => i.visible !== false) || [
    { id: "1", label: { en: "About", de: "\u00dcber uns", ar: "\u0645\u0646 \u0646\u062d\u0646" }, href: "#about" },
    { id: "2", label: { en: "Services", de: "Leistungen", ar: "\u0627\u0644\u062e\u062f\u0645\u0627\u062a" }, href: "#services" },
    { id: "3", label: { en: "Portfolio", de: "Projekte", ar: "\u0627\u0644\u0645\u0634\u0627\u0631\u064a\u0639" }, href: "#portfolio" },
    { id: "4", label: { en: "Contact", de: "Kontakt", ar: "\u0627\u062a\u0635\u0644 \u0628\u0646\u0627" }, href: "#contact" }
  ];

  const handleNavClick = (href) => {
    setMobileMenuOpen(false);
    if (href.startsWith('#')) {
      const id = href.replace('#', '');
      if (window.location.pathname === '/') {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = `/#${id}`;
      }
    } else if (href.startsWith('http')) {
      window.open(href, '_blank');
    } else {
      window.location.href = href;
    }
  };

  return (
    <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-mmg-mine-shaft">
      <div className="flex items-center justify-between h-20 px-6 md:px-12 max-w-[1920px] mx-auto">
        <a href="/" data-testid="logo" className="flex items-center">
          <img src="/logo.png" alt="Mahzod Media Group" className="h-14 md:h-16 w-auto object-contain invert" />
        </a>
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item, idx) => (
            <button key={item.id || idx} data-testid={`nav-item-${idx}`} onClick={() => handleNavClick(item.href)}
              className="font-heading font-medium text-sm uppercase tracking-widest text-mmg-silver-chalice hover:text-white transition-colors">
              {item.label?.[language] || item.label?.en || ""}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-4 border-l border-mmg-mine-shaft pl-4">
            {["en", "de", "ar"].map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="text-mmg-boulder">|</span>}
                <button data-testid={`lang-${l}`} onClick={() => setLanguage(l)}
                  className={`font-heading font-medium text-sm uppercase tracking-widest transition-colors ${language === l ? 'text-white' : 'text-mmg-silver-chalice hover:text-white'}`}>
                  {l.toUpperCase()}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-white p-2">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {mobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-black border-t border-mmg-mine-shaft">
          <div className="flex flex-col py-6 px-6 gap-4">
            {navItems.map((item, idx) => (
              <button key={item.id || idx} data-testid={`mobile-nav-${idx}`} onClick={() => handleNavClick(item.href)}
                className="font-heading font-medium text-lg uppercase tracking-widest text-white text-left py-2">
                {item.label?.[language] || item.label?.en || ""}
              </button>
            ))}
            <div className="flex items-center gap-4 pt-4 border-t border-mmg-mine-shaft">
              {["en", "de", "ar"].map((l, i) => (
                <React.Fragment key={l}>
                  {i > 0 && <span className="text-mmg-boulder">|</span>}
                  <button data-testid={`mobile-lang-${l}`} onClick={() => setLanguage(l)}
                    className={`font-heading font-medium text-lg uppercase tracking-widest ${language === l ? 'text-white' : 'text-mmg-silver-chalice'}`}>
                    {l.toUpperCase()}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { t, language } = useLanguage();
  const sc = useSiteContent();
  const hero = sc?.hero;
  const tagline = hero?.tagline?.[language] || t.hero.tagline;
  const headline = hero?.headline?.[language] || t.hero.headline;
  const cta = hero?.cta?.[language] || t.hero.cta;
  const bgImage = hero?.background_image || 'https://images.unsplash.com/photo-1613969673101-c982c30287f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBpbm5vdmF0aW9uJTIwbWluaW1hbGlzdCUyMGFyY2hpdGVjdHVyZSUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzcwODk5NDg0fDA&ixlib=rb-4.1.0&q=85';

  const scrollToPortfolio = () => {
    const element = document.getElementById('portfolio');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${bgImage}')` }}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 text-center px-6 md:px-12 max-w-6xl mx-auto">
        <p data-testid="hero-tagline" className="font-heading font-medium text-sm md:text-base uppercase tracking-[0.3em] text-mmg-silver-chalice mb-8 opacity-0 animate-fade-in-up">
          {tagline}
        </p>
        <h1 data-testid="hero-headline" className={`font-heading font-bold text-4xl md:text-6xl lg:text-7xl xl:text-8xl uppercase tracking-tight leading-[0.95] text-white mb-12 opacity-0 animate-fade-in-up animate-delay-200 ${t.dir === 'rtl' ? 'font-sans' : ''}`}
          dangerouslySetInnerHTML={{ __html: headline }}
        />
        <button data-testid="hero-cta" onClick={scrollToPortfolio}
          className="group inline-flex items-center gap-3 border border-white bg-white text-black px-8 py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 opacity-0 animate-fade-in-up animate-delay-400">
          <span>{cta}</span>
          <ArrowRight size={18} className={`group-hover:translate-x-2 transition-transform duration-300 ${t.dir === 'rtl' ? 'rotate-180 group-hover:-translate-x-2' : ''}`} />
        </button>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-0 animate-fade-in animate-delay-500">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-mmg-silver-chalice to-transparent" />
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t, language } = useLanguage();
  const sc = useSiteContent();
  const about = sc?.about;
  const title = about?.title?.[language] || t.about.title;
  const description = about?.description?.[language] || t.about.description;
  const image = about?.image || 'https://images.unsplash.com/photo-1542342061607-904c53a454c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBpbm5vdmF0aW9uJTIwbWluaW1hbGlzdCUyMGFyY2hpdGVjdHVyZSUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzcwODk5NDg0fDA&ixlib=rb-4.1.0&q=85';

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 lg:py-40 bg-black">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div>
            <h2 data-testid="about-title" className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-8">
              {title}
            </h2>
            <p data-testid="about-description" className="font-body text-base md:text-lg leading-relaxed text-mmg-silver-chalice">
              {description}
            </p>
          </div>
          <div className="relative aspect-[4/3] lg:aspect-square overflow-hidden">
            <img data-testid="about-image" src={image} alt="About" className="w-full h-full object-cover" />
            <div className="absolute inset-0 border border-mmg-mine-shaft" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t, language } = useLanguage();
  const sc = useSiteContent();
  const svc = sc?.services;

  const ICON_MAP = { ShoppingCart, Package, Truck, Palette, Cpu, Settings };
  const defaultIcons = ["ShoppingCart", "Package", "Truck", "Palette", "Cpu", "Settings"];

  const sectionTitle = svc?.title?.[language] || t.services.title;
  const items = svc?.items || t.services.items.map((s, i) => ({
    id: String(i), title: { [language]: s.title }, desc: { [language]: s.desc }, icon: defaultIcons[i]
  }));

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 lg:py-40 bg-mmg-cod-gray">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        <h2 data-testid="services-title" className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-16 text-center">
          {sectionTitle}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-mmg-mine-shaft border border-mmg-mine-shaft">
          {items.map((service, index) => {
            const IconComp = ICON_MAP[service.icon] || Settings;
            return (
              <button key={service.id || index} data-testid={`service-card-${index}`} onClick={scrollToContact}
                className="service-card group relative bg-black p-8 md:p-12 flex flex-col text-left hover:bg-mmg-cod-gray transition-colors duration-500 min-h-[300px] cursor-pointer">
                <div className="text-white mb-6"><IconComp size={32} strokeWidth={1} /></div>
                <h3 className="font-heading font-semibold text-xl md:text-2xl uppercase tracking-tight text-white mb-4">
                  {service.title?.[language] || service.title?.en || ""}
                </h3>
                <p className="font-body text-sm md:text-base text-mmg-silver-chalice leading-relaxed">
                  {service.desc?.[language] || service.desc?.en || ""}
                </p>
                <ArrowRight size={20} className={`absolute bottom-8 ${t.dir === 'rtl' ? 'left-8 rotate-180 group-hover:-translate-x-2' : 'right-8 group-hover:translate-x-2'} text-mmg-boulder group-hover:text-white transition-all duration-300`} />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const { t, language } = useLanguage();
  const sc = useSiteContent();
  const pf = sc?.portfolio;

  const sectionTitle = pf?.title?.[language] || t.portfolio.title;
  const sectionDesc = pf?.description?.[language] || t.portfolio.description;
  const items = pf?.items || [
    { id: "1", name: "MISR VERA", category: { en: "Luxury Brand", de: "Luxusmarke" }, logo: "/misr-vera-logo.png", url: "#" },
    { id: "2", name: "Dr. Mohamed Abbas", category: { en: "Medical Practice", de: "Medizinische Praxis" }, logo: "/dr-abbas-logo.png", url: "https://dr-mohamed-abbas.com" }
  ];

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 md:py-32 lg:py-40 bg-black">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <h2 data-testid="portfolio-title" className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-8">{sectionTitle}</h2>
          <p data-testid="portfolio-description" className="font-body text-base md:text-lg leading-relaxed text-mmg-silver-chalice max-w-3xl mx-auto">{sectionDesc}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((item, idx) => (
            <a key={item.id || idx} href={item.url || "#"}
              target={item.url && item.url !== '#' ? '_blank' : undefined}
              rel={item.url && item.url !== '#' ? 'noopener noreferrer' : undefined}
              data-testid={`portfolio-card-${idx + 1}`}
              className="portfolio-card group relative overflow-hidden h-[400px] md:h-[500px] border border-mmg-mine-shaft hover:border-white transition-colors duration-500">
              <div className="absolute inset-0 bg-black flex items-center justify-center p-12">
                {item.logo ? <img src={item.logo} alt={item.name} className="max-w-[80%] max-h-[60%] object-contain portfolio-image" /> : <span className="font-heading text-4xl text-white uppercase">{item.name}</span>}
              </div>
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8">
                <span className="font-heading text-xs uppercase tracking-[0.3em] text-mmg-silver-chalice mb-2">{item.category?.[language] || item.category?.en || ""}</span>
                <h3 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight text-white">{item.name}</h3>
                {item.url && item.url !== '#' && <span className="font-body text-sm text-mmg-silver-chalice mt-2 flex items-center gap-2">{item.url.replace('https://', '')}<ArrowRight size={14} /></span>}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t, language } = useLanguage();
  const sc = useSiteContent();
  const ct = sc?.contact;
  const contactTitle = ct?.title?.[language] || t.contact.title;
  const contactSubtitle = ct?.subtitle?.[language] || t.contact.subtitle;
  const [formData, setFormData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    phone: '',
    company: '',
    city: '',
    country: '',
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, answer: '' });
  const [captchaError, setCaptchaError] = useState(false);

  // Generate new CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2, answer: '' });
    setCaptchaError(false);
  };

  // Initialize CAPTCHA on mount
  React.useEffect(() => {
    generateCaptcha();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verify CAPTCHA
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(captcha.answer) !== correctAnswer) {
      setCaptchaError(true);
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.contact.success);
      setFormData({ 
        firstName: '', 
        lastName: '', 
        email: '', 
        phone: '',
        company: '',
        city: '',
        country: '',
        message: '' 
      });
      generateCaptcha();
    } catch (error) {
      toast.error(t.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 lg:py-40 bg-mmg-cod-gray">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Info */}
          <div>
            <h2 
              data-testid="contact-title"
              className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-4"
            >
              {contactTitle}
            </h2>
            <p 
              data-testid="contact-subtitle"
              className="font-body text-base md:text-lg text-mmg-silver-chalice mb-12"
            >
              {contactSubtitle}
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-6">
              <a
                href="https://www.instagram.com/mahzodmediagroup"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="text-mmg-silver-chalice hover:text-white transition-colors"
              >
                <Instagram size={24} strokeWidth={1.5} />
              </a>
              <a
                href="https://www.linkedin.com/company/mahzod-media-group"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="text-mmg-silver-chalice hover:text-white transition-colors"
              >
                <Linkedin size={24} strokeWidth={1.5} />
              </a>
              <a
                href="mailto:info@mahzod.com"
                data-testid="social-email"
                className="text-mmg-silver-chalice hover:text-white transition-colors"
              >
                <Mail size={24} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} data-testid="contact-form" className="flex flex-col gap-6">
            <p className="font-body text-sm text-mmg-silver-chalice mb-2">{t.contact.required}</p>
            
            {/* Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <input
                  type="text"
                  name="firstName"
                  data-testid="contact-firstname-input"
                  placeholder={`${t.contact.firstName} *`}
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="lastName"
                  data-testid="contact-lastname-input"
                  placeholder={`${t.contact.lastName} *`}
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  required
                  className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                data-testid="contact-email-input"
                placeholder={`${t.contact.email} *`}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
              />
            </div>

            {/* Phone */}
            <div>
              <input
                type="tel"
                name="phone"
                data-testid="contact-phone-input"
                placeholder={t.contact.phone}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
              />
            </div>

            {/* Company */}
            <div>
              <input
                type="text"
                name="company"
                data-testid="contact-company-input"
                placeholder={t.contact.company}
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
              />
            </div>

            {/* City & Country Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <input
                  type="text"
                  name="city"
                  data-testid="contact-city-input"
                  placeholder={t.contact.city}
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="country"
                  data-testid="contact-country-input"
                  placeholder={`${t.contact.country} *`}
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                  className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <textarea
                name="message"
                data-testid="contact-message-input"
                placeholder={t.contact.message}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={5}
                className="form-input w-full py-4 font-body text-base text-white placeholder:text-mmg-silver-chalice resize-none"
              />
            </div>

            {/* CAPTCHA */}
            <div className="bg-black p-6 border border-mmg-mine-shaft">
              <p className="font-body text-sm text-mmg-silver-chalice mb-3">
                {t.contact.captcha || 'Security Question'} *
              </p>
              <div className="flex items-center gap-4">
                <span className="font-heading font-bold text-xl text-white">
                  {captcha.num1} + {captcha.num2} =
                </span>
                <input
                  type="number"
                  data-testid="contact-captcha-input"
                  value={captcha.answer}
                  onChange={(e) => setCaptcha({ ...captcha, answer: e.target.value })}
                  required
                  className="form-input w-24 py-2 px-3 font-body text-base text-white placeholder:text-mmg-silver-chalice text-center"
                  placeholder="?"
                />
                <button
                  type="button"
                  onClick={generateCaptcha}
                  className="text-mmg-silver-chalice hover:text-white transition-colors p-2"
                  title="New question"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                    <path d="M21 3v5h-5"/>
                  </svg>
                </button>
              </div>
              {captchaError && (
                <p className="font-body text-sm text-red-500 mt-2">
                  {t.contact.captchaError || 'Incorrect answer. Please try again.'}
                </p>
              )}
            </div>

            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={isSubmitting}
              className="self-start border border-white bg-white text-black px-8 py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '...' : t.contact.send}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

// Brand Guidelines Section
const BrandSection = () => {
  const { t } = useLanguage();

  const primaryColors = [
    { name: 'Black', hex: '#000000' },
    { name: 'White', hex: '#FFFFFF' }
  ];

  const secondaryColors = [
    { name: 'Cod Gray', hex: '#141414' },
    { name: 'Mine Shaft', hex: '#262626' },
    { name: 'Boulder', hex: '#4E4E4E' },
    { name: 'Silver Chalice', hex: '#9E9E9E' },
    { name: 'Silver', hex: '#C3C3C3' },
    { name: 'Mercury', hex: '#E7E7E7' }
  ];

  return (
    <section id="brand" data-testid="brand-section" className="py-24 md:py-32 lg:py-40 bg-black">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 
            data-testid="brand-title"
            className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-6"
          >
            {t.brand.title}
          </h2>
          <p className="font-body text-base md:text-lg text-mmg-silver-chalice max-w-3xl mx-auto">
            {t.brand.subtitle}
          </p>
        </div>

        {/* Color Palette */}
        <div className="mb-20">
          <h3 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight text-white mb-8">
            {t.brand.colors.title}
          </h3>
          
          {/* Primary Colors */}
          <div className="mb-8">
            <p className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-4">
              {t.brand.colors.primary}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {primaryColors.map((color) => (
                <div key={color.hex} data-testid={`color-primary-${color.hex}`} className="group">
                  <div 
                    className="aspect-square border border-mmg-mine-shaft mb-3"
                    style={{ backgroundColor: color.hex }}
                  />
                  <p className="font-heading text-sm text-white">{color.name}</p>
                  <p className="font-body text-xs text-mmg-silver-chalice">{color.hex}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Secondary Colors */}
          <div>
            <p className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-4">
              {t.brand.colors.secondary}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {secondaryColors.map((color) => (
                <div key={color.hex} data-testid={`color-secondary-${color.hex}`} className="group">
                  <div 
                    className="aspect-square border border-mmg-mine-shaft mb-3"
                    style={{ backgroundColor: color.hex }}
                  />
                  <p className="font-heading text-sm text-white">{color.name}</p>
                  <p className="font-body text-xs text-mmg-silver-chalice">{color.hex}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Typography */}
        <div className="mb-20">
          <h3 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight text-white mb-8">
            {t.brand.typography.title}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Headings */}
            <div className="bg-mmg-cod-gray p-8 border border-mmg-mine-shaft">
              <p className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-4">
                {t.brand.typography.headings}
              </p>
              <p className="font-heading font-bold text-4xl text-white mb-2">Barlow Condensed</p>
              <p className="font-heading text-xl text-mmg-silver-chalice mb-4">Bold / Semibold / Medium</p>
              <p className="font-heading font-bold text-2xl uppercase tracking-tight text-white">
                ABCDEFGHIJKLMNOPQRSTUVWXYZ
              </p>
              <p className="font-heading text-lg text-mmg-silver-chalice mt-2">
                {t.brand.typography.sample}
              </p>
            </div>

            {/* Body */}
            <div className="bg-mmg-cod-gray p-8 border border-mmg-mine-shaft">
              <p className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-4">
                {t.brand.typography.body}
              </p>
              <p className="font-body font-semibold text-4xl text-white mb-2">Barlow</p>
              <p className="font-body text-xl text-mmg-silver-chalice mb-4">Regular / Light</p>
              <p className="font-body text-lg text-white">
                abcdefghijklmnopqrstuvwxyz
              </p>
              <p className="font-body text-lg text-white">
                0123456789
              </p>
              <p className="font-body text-base text-mmg-silver-chalice mt-2">
                {t.brand.typography.sample}
              </p>
            </div>
          </div>
        </div>

        {/* Logo Usage */}
        <div className="mb-20">
          <h3 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight text-white mb-8">
            {t.brand.logo.title}
          </h3>

          {/* Logo Versions */}
          <p className="font-heading text-sm uppercase tracking-widest text-mmg-silver-chalice mb-4">
            {t.brand.logo.versions}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Primary Logo */}
            <div className="bg-white p-12 flex items-center justify-center">
              <img 
                src="/logo.png"
                alt="Primary Logo"
                className="h-24 w-auto object-contain"
              />
            </div>
            {/* Inverted Logo */}
            <div className="bg-black p-12 flex items-center justify-center border border-mmg-mine-shaft">
              <img 
                src="/logo.png"
                alt="Inverted Logo"
                className="h-24 w-auto object-contain invert"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
            <p className="font-heading text-sm text-center text-mmg-silver-chalice">{t.brand.logo.primary}</p>
            <p className="font-heading text-sm text-center text-mmg-silver-chalice">{t.brand.logo.inverted}</p>
          </div>

          {/* Clear Space & Min Size */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            <div className="bg-mmg-cod-gray p-8 border border-mmg-mine-shaft">
              <p className="font-heading text-lg uppercase tracking-tight text-white mb-2">
                {t.brand.logo.clearSpace}
              </p>
              <p className="font-body text-sm text-mmg-silver-chalice">
                {t.brand.logo.clearSpaceDesc}
              </p>
            </div>
            <div className="bg-mmg-cod-gray p-8 border border-mmg-mine-shaft">
              <p className="font-heading text-lg uppercase tracking-tight text-white mb-2">
                {t.brand.logo.minSize}
              </p>
              <p className="font-body text-sm text-mmg-silver-chalice">
                {t.brand.logo.minSizeDesc}
              </p>
            </div>
          </div>
        </div>

        {/* Do's and Don'ts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Do's */}
          <div className="bg-mmg-cod-gray p-8 border border-mmg-mine-shaft">
            <div className="flex items-center gap-3 mb-6">
              <Check size={24} className="text-green-500" />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight text-white">
                {t.brand.dos.title}
              </h3>
            </div>
            <ul className="space-y-3">
              {t.brand.dos.items.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-mmg-silver-chalice">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Don'ts */}
          <div className="bg-mmg-cod-gray p-8 border border-mmg-mine-shaft">
            <div className="flex items-center gap-3 mb-6">
              <X size={24} className="text-red-500" />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight text-white">
                {t.brand.donts.title}
              </h3>
            </div>
            <ul className="space-y-3">
              {t.brand.donts.items.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <X size={16} className="text-red-500 mt-1 flex-shrink-0" />
                  <span className="font-body text-sm text-mmg-silver-chalice">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Section
const Footer = () => {
  const { t, language } = useLanguage();
  const sc = useSiteContent();
  const ft = sc?.footer;
  const currentYear = new Date().getFullYear();

  const tagline = ft?.tagline?.[language] || t.footer.tagline;
  const copyright = ft?.copyright?.[language] || t.footer.copyright;
  
  const socialLinks = ft?.social_links?.filter(l => l.visible !== false) || [
    { platform: "instagram", url: "https://www.instagram.com/mahzodmediagroup" },
    { platform: "linkedin", url: "https://www.linkedin.com/company/mahzod-media-group" }
  ];

  const footerLinks = ft?.links?.filter(l => l.visible !== false) || [
    { label: { en: "Imprint", de: "Impressum" }, href: "/impressum" },
    { label: { en: "Brand Guidelines", de: "Markenrichtlinien" }, href: "/brand" },
    { label: { en: "Privacy Policy", de: "Datenschutz" }, href: "/privacy" },
    { label: { en: "Jobs", de: "Jobs" }, href: "/jobs" }
  ];

  const SOCIAL_ICONS = { instagram: Instagram, linkedin: Linkedin, email: Mail, twitter: Twitter, facebook: Facebook, youtube: Youtube };

  return (
    <footer data-testid="footer" className="py-16 md:py-24 bg-black border-t border-mmg-mine-shaft">
      <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-12">
          <img data-testid="footer-logo" src="/logo.png" alt="Mahzod Media Group" className="h-24 md:h-32 lg:h-40 w-auto object-contain invert" />
        </div>
        <div className="flex flex-col md:flex-row gap-6 md:gap-12 mb-12 pb-12 border-b border-mmg-mine-shaft">
          {footerLinks.map((link) => (
            <a key={link.href} href={link.href} data-testid={`footer-link-${link.href}`}
              className="font-heading font-medium text-sm uppercase tracking-widest text-mmg-silver-chalice hover:text-white transition-colors">
              {link.label?.[language] || link.label?.en || ""}
            </a>
          ))}
          <a href="/portal" data-testid="footer-portal-link"
            className="font-heading font-medium text-sm uppercase tracking-widest text-mmg-silver-chalice hover:text-white transition-colors">
            Client Portal
          </a>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div>
            <p data-testid="footer-tagline" className="font-body text-base text-mmg-silver-chalice mb-2">{tagline}</p>
            <p data-testid="footer-copyright" className="font-body text-sm text-mmg-boulder">&copy; {currentYear} {copyright}</p>
          </div>
          <div className="flex items-center gap-6">
            {socialLinks.map((link) => {
              const Icon = SOCIAL_ICONS[link.platform] || Globe;
              const href = link.platform === 'email' ? (link.url.startsWith('mailto:') ? link.url : `mailto:${link.url}`) : link.url;
              return (
                <a key={link.platform} href={href} target={link.platform !== 'email' ? '_blank' : undefined} rel="noopener noreferrer"
                  data-testid={`footer-social-${link.platform}`} className="text-mmg-boulder hover:text-white transition-colors">
                  <Icon size={20} strokeWidth={1.5} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Page
const Home = () => {
  // Handle hash navigation on page load
  React.useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div data-testid="main-content">
      <Navbar />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <PortfolioSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Brand Guidelines Page
const BrandPage = () => {
  return (
    <div data-testid="brand-page">
      <Navbar />
      <main className="pt-20">
        <BrandSection />
      </main>
      <Footer />
    </div>
  );
};

// Imprint Page - Dynamic with CMS content and Company Info
const ImprintPage = () => {
  const { t, language } = useLanguage();
  const [cmsContent, setCmsContent] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const [cmsRes, companyRes] = await Promise.all([
          axios.get(`${API}/legal/impressum`),
          axios.get(`${API}/company-info/public`)
        ]);
        if (cmsRes.data && cmsRes.data.content) {
          setCmsContent(cmsRes.data);
        }
        if (companyRes.data) {
          setCompanyInfo(companyRes.data);
        }
      } catch (e) { }
      setLoading(false);
    };
    fetchContent();
  }, [language]);

  // If CMS has content for current language, show it
  const hasCmsContent = cmsContent?.content?.[language] && cmsContent.content[language].trim().length > 0;
  
  // Use company info from API or fallback to translations
  const company = companyInfo || {};
  const ceoLabel = language === "de" ? "Inhaber / Geschäftsführer" : "Owner / CEO";
  const legalFormLabel = language === "de" ? "Rechtsform" : "Legal Form";

  return (
    <div data-testid="imprint-page">
      <Navbar />
      <main className="pt-20">
        <section className="py-24 md:py-32 bg-black min-h-screen">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h1 className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-12">
              {cmsContent?.meta_title?.[language] || t.imprint.title}
            </h1>

            {loading ? (
              <p className="text-mmg-silver-chalice">Loading...</p>
            ) : hasCmsContent ? (
              <div 
                className="prose prose-invert max-w-none text-mmg-silver-chalice 
                           prose-headings:text-white prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-tight
                           prose-h2:text-xl prose-h2:font-semibold prose-h2:mb-4 prose-h2:pb-4 prose-h2:border-b prose-h2:border-mmg-mine-shaft
                           prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3
                           prose-p:font-body prose-p:text-base prose-p:leading-relaxed prose-p:text-mmg-silver-chalice
                           prose-a:text-white prose-a:hover:opacity-80 prose-a:transition-colors prose-a:no-underline
                           prose-strong:text-white prose-strong:font-semibold
                           prose-blockquote:bg-mmg-cod-gray prose-blockquote:border-l-4 prose-blockquote:border-mmg-mine-shaft prose-blockquote:p-6 prose-blockquote:my-6 prose-blockquote:not-italic
                           prose-ul:space-y-2 prose-li:text-mmg-silver-chalice
                           [&>h2]:mb-12 [&>div]:mb-12 [&>section]:mb-12"
                dangerouslySetInnerHTML={{ __html: cmsContent.content[language] }}
              />
            ) : (
              <>
                {/* Company Info - Dynamic from API */}
                <div className="mb-12 pb-12 border-b border-mmg-mine-shaft">
                  <p className="font-heading font-semibold text-xl text-white mb-4">
                    {company.name || t.imprint.company}
                    {company.show_legal_form && company.legal_form && company.legal_form !== "Einzelunternehmen" && ` ${company.legal_form}`}
                  </p>
                  <p className="font-body text-base text-mmg-silver-chalice">{company.street || t.imprint.address}</p>
                  <p className="font-body text-base text-mmg-silver-chalice">{company.zip} {company.city || t.imprint.city}</p>
                  <p className="font-body text-base text-mmg-silver-chalice mb-6">{company.country || t.imprint.country}</p>
                  
                  <div className="space-y-2">
                    {company.show_legal_form && company.legal_form && (
                      <p className="font-body text-base text-mmg-silver-chalice">
                        <span className="text-white">{legalFormLabel}:</span> {company.legal_form}
                      </p>
                    )}
                    {company.tax_number && (
                      <p className="font-body text-base text-mmg-silver-chalice">
                        <span className="text-white">{t.imprint.taxNumber}:</span> {company.tax_number}
                      </p>
                    )}
                    {company.vat_id && (
                      <p className="font-body text-base text-mmg-silver-chalice">
                        <span className="text-white">{t.imprint.vatId}:</span> {company.vat_id}
                      </p>
                    )}
                    {company.hrb_number && (
                      <p className="font-body text-base text-mmg-silver-chalice">
                        <span className="text-white">Handelsregister:</span> {company.hrb_number}
                        {company.registry_court && `, ${company.registry_court}`}
                      </p>
                    )}
                  </div>
                </div>

                {/* Management / CEO */}
                <div className="mb-12 pb-12 border-b border-mmg-mine-shaft">
                  <h2 className="font-heading font-semibold text-xl uppercase tracking-tight text-white mb-4">
                    {ceoLabel}
                  </h2>
                  <p className="font-body text-base text-mmg-silver-chalice">{company.ceo_name || "Mohab Hoffmann-Yassin"}</p>
                </div>

                {/* Contact */}
                <div className="mb-12 pb-12 border-b border-mmg-mine-shaft">
                  <h2 className="font-heading font-semibold text-xl uppercase tracking-tight text-white mb-4">
                    {t.imprint.contact}
                  </h2>
                  {company.email && (
                    <p className="font-body text-base text-mmg-silver-chalice">
                      <span className="text-white">{t.imprint.email}:</span>{' '}
                      <a href={`mailto:${company.email}`} className="hover:text-white transition-colors">
                        {company.email}
                      </a>
                    </p>
                  )}
                  {company.phone && (
                    <p className="font-body text-base text-mmg-silver-chalice mt-2">
                      <span className="text-white">{language === "de" ? "Telefon" : "Phone"}:</span>{' '}
                      <a href={`tel:${company.phone}`} className="hover:text-white transition-colors">
                        {company.phone}
                      </a>
                    </p>
                  )}
                  {company.website && (
                    <p className="font-body text-base text-mmg-silver-chalice mt-2">
                      <span className="text-white">Website:</span>{' '}
                      <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                        {company.website}
                      </a>
                    </p>
                  )}
                </div>

                {/* Disclaimer */}
                <div>
                  <h2 className="font-heading font-semibold text-2xl uppercase tracking-tight text-white mb-8">
                    {t.imprint.disclaimer.title}
                  </h2>
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-white mb-3">
                        {t.imprint.disclaimer.liability.title}
                      </h3>
                      <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                        {t.imprint.disclaimer.liability.text}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-white mb-3">
                        {t.imprint.disclaimer.links.title}
                      </h3>
                      <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                        {t.imprint.disclaimer.links.text}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-white mb-3">
                        {t.imprint.disclaimer.copyright.title}
                      </h3>
                      <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                        {t.imprint.disclaimer.copyright.text}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

// Privacy Policy Page - Dynamic with CMS content
const PrivacyPage = () => {
  const { t, language } = useLanguage();
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/legal/privacy`);
        if (res.data && res.data.content) {
          setCmsContent(res.data);
        }
      } catch (e) { }
      setLoading(false);
    };
    fetchContent();
  }, [language]);

  const hasCmsContent = cmsContent?.content?.[language] && cmsContent.content[language].trim().length > 0;

  return (
    <div data-testid="privacy-page">
      <Navbar />
      <main className="pt-20">
        <section className="py-24 md:py-32 bg-black min-h-screen">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h1 className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-8">
              {cmsContent?.meta_title?.[language] || t.privacy.title}
            </h1>

            {loading ? (
              <p className="text-mmg-silver-chalice">Loading...</p>
            ) : hasCmsContent ? (
              <div 
                className="prose prose-invert max-w-none text-mmg-silver-chalice 
                           prose-headings:text-white prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-tight
                           prose-h2:text-xl prose-h2:font-semibold prose-h2:mb-6
                           prose-h3:text-lg prose-h3:font-semibold prose-h3:mb-3
                           prose-p:font-body prose-p:text-base prose-p:leading-relaxed prose-p:text-mmg-silver-chalice
                           prose-a:text-white prose-a:hover:opacity-80 prose-a:transition-colors prose-a:no-underline
                           prose-strong:text-white prose-strong:font-semibold
                           prose-blockquote:bg-mmg-cod-gray prose-blockquote:border-l-4 prose-blockquote:border-mmg-mine-shaft prose-blockquote:p-6 prose-blockquote:my-6 prose-blockquote:not-italic
                           prose-ul:space-y-2 prose-li:text-mmg-silver-chalice
                           [&>p:first-of-type]:mb-12 [&>h2]:mb-12 [&>div]:mb-12 [&>section]:mb-12"
                dangerouslySetInnerHTML={{ __html: cmsContent.content[language] }}
              />
            ) : (
              <>
                <p className="font-body text-base text-mmg-silver-chalice leading-relaxed mb-12">
                  {t.privacy.intro}
                </p>

                {/* Responsible Party */}
                <div className="mb-12">
                  <h2 className="font-heading font-semibold text-xl uppercase tracking-tight text-white mb-4">
                    {t.privacy.sections.responsible.title}
                  </h2>
                  <p className="font-body text-base text-mmg-silver-chalice mb-4">
                    {t.privacy.sections.responsible.text}
                  </p>
                  <div className="bg-mmg-cod-gray p-6 border border-mmg-mine-shaft">
                    <p className="font-body text-base text-white">Mahzod Media Group</p>
                    <p className="font-body text-base text-mmg-silver-chalice">Hügelstraße 32</p>
                    <p className="font-body text-base text-mmg-silver-chalice">49497 Mettingen</p>
                    <p className="font-body text-base text-mmg-silver-chalice mt-2">
                      E-Mail: <a href="mailto:info@mahzod.com" className="hover:text-white transition-colors">info@mahzod.com</a>
                    </p>
                  </div>
                </div>

                {/* Data Collection */}
                <div className="mb-12">
                  <h2 className="font-heading font-semibold text-xl uppercase tracking-tight text-white mb-6">
                    {t.privacy.sections.dataCollection.title}
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-white mb-3">
                        {t.privacy.sections.dataCollection.cookies.title}
                      </h3>
                      <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                        {t.privacy.sections.dataCollection.cookies.text}
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-white mb-3">
                        {t.privacy.sections.dataCollection.server.title}
                      </h3>
                      <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                        {t.privacy.sections.dataCollection.server.text}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Your Rights */}
                <div className="mb-12">
                  <h2 className="font-heading font-semibold text-xl uppercase tracking-tight text-white mb-4">
                    {t.privacy.sections.rights.title}
                  </h2>
                  <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                    {t.privacy.sections.rights.text}
                  </p>
                </div>

                {/* Contact Form */}
                <div>
                  <h2 className="font-heading font-semibold text-xl uppercase tracking-tight text-white mb-4">
                    {t.privacy.sections.contact.title}
                  </h2>
                  <p className="font-body text-base text-mmg-silver-chalice leading-relaxed">
                    {t.privacy.sections.contact.text}
                  </p>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

// Jobs Page
const JobsPage = () => {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/public/jobs`).then(res => { setJobs(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = jobs.filter(j => 
    j.title?.toLowerCase().includes(search.toLowerCase()) ||
    j.office?.toLowerCase().includes(search.toLowerCase()) ||
    j.function?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="jobs-page">
      <Navbar />
      <main className="pt-20">
        <section className="py-16 md:py-24 bg-black min-h-screen">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-white mb-8">
              {t.jobs.currentPositions}
            </h1>

            {jobs.length > 0 && (
              <div className="mb-8">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t.jobs.searchPlaceholder}
                  className="w-full py-3 px-4 font-body text-base text-white placeholder:text-mmg-silver-chalice bg-black border border-mmg-mine-shaft"
                  data-testid="jobs-search"
                />
              </div>
            )}

            {loading ? (
              <div className="text-center py-16 text-mmg-silver-chalice font-heading">Loading...</div>
            ) : filtered.length > 0 ? (
              <div className="space-y-4 mb-12">
                <p className="text-mmg-silver-chalice font-body text-sm">{filtered.length} {t.jobs.jobsAvailable}</p>
                {filtered.map((job) => (
                  <a
                    key={job.id}
                    href={`/jobs/${job.id}`}
                    className="block border border-mmg-mine-shaft p-6 hover:border-white transition-colors group"
                    data-testid={`job-card-${job.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        {job.featured && (
                          <span className="inline-flex items-center gap-1 text-xs font-heading uppercase tracking-widest text-yellow-400 mb-2">
                            <Star size={12} fill="currentColor" /> {t.jobs.featured}
                          </span>
                        )}
                        <h3 className="font-heading font-bold text-lg text-white group-hover:text-mmg-silver-chalice transition-colors">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-mmg-silver-chalice font-body">
                          <span className="flex items-center gap-1"><MapPin size={14} /> {job.office}</span>
                          <span className="flex items-center gap-1"><Briefcase size={14} /> {job.function}</span>
                          <span className="flex items-center gap-1"><Clock size={14} /> {job.type}</span>
                        </div>
                      </div>
                      <ArrowRight size={20} className="text-mmg-silver-chalice group-hover:text-white transition-colors mt-2" />
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 border border-mmg-mine-shaft mb-12">
                <h3 className="font-heading font-bold text-3xl text-white mb-4">
                  0 {t.jobs.jobsAvailable}
                </h3>
                <p className="font-body text-base text-mmg-silver-chalice">{t.jobs.noJobs}</p>
              </div>
            )}

            <div className="text-center pt-8">
              <p className="font-body text-base text-mmg-silver-chalice mb-6">{t.jobs.talentText}</p>
              <a 
                href="mailto:info@mahzod.com"
                className="inline-flex items-center gap-3 border border-white bg-white text-black px-8 py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
              >
                <Mail size={18} />
                <span>{t.jobs.email}</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

// Job Detail Page
const JobDetailPage = () => {
  const { jobId } = useParams();
  const { t, lang } = useLanguage();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', cover_letter: '', resume_url: ''
  });
  
  // Simple Math CAPTCHA
  const [captchaNum1] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [captchaNum2] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/jobs/${jobId}`).then(res => { setJob(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, [jobId]);

  const handleApply = async (e) => {
    e.preventDefault();
    
    // Verify CAPTCHA
    if (parseInt(captchaAnswer) !== captchaNum1 + captchaNum2) {
      toast.error(lang === "de" ? "Falsche CAPTCHA-Antwort" : "Incorrect CAPTCHA answer");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/applications`, { ...formData, job_id: jobId });
      toast.success(t.jobs.applicationSuccess);
      setShowForm(false);
      setFormData({ first_name: '', last_name: '', email: '', phone: '', cover_letter: '', resume_url: '' });
      setCaptchaAnswer("");
    } catch (err) {
      toast.error(t.jobs.applicationError);
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div data-testid="job-detail-page">
      <Navbar />
      <main className="pt-20 min-h-screen bg-black flex items-center justify-center">
        <div className="text-mmg-silver-chalice font-heading">Loading...</div>
      </main>
    </div>
  );

  if (!job) return (
    <div data-testid="job-detail-page">
      <Navbar />
      <main className="pt-20 min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white font-heading text-xl mb-4">Job not found</p>
          <a href="/jobs" className="text-mmg-silver-chalice hover:text-white font-heading uppercase text-sm">{t.jobs.backToJobs}</a>
        </div>
      </main>
    </div>
  );

  return (
    <div data-testid="job-detail-page">
      <Navbar />
      <main className="pt-20">
        <section className="py-16 md:py-24 bg-black min-h-screen">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            {/* Back link */}
            <a href="/jobs" className="inline-flex items-center gap-2 text-mmg-silver-chalice hover:text-white font-heading text-sm uppercase tracking-widest mb-8 transition-colors" data-testid="back-to-jobs">
              <ArrowLeft size={16} /> {t.jobs.backToJobs}
            </a>

            {/* Job header */}
            <div className="mb-10">
              {job.featured && (
                <span className="inline-flex items-center gap-1 text-xs font-heading uppercase tracking-widest text-yellow-400 mb-3">
                  <Star size={12} fill="currentColor" /> {t.jobs.featured}
                </span>
              )}
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">{job.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-mmg-silver-chalice font-body">
                <span className="flex items-center gap-1"><MapPin size={14} /> {job.office}</span>
                <span className="flex items-center gap-1"><Briefcase size={14} /> {job.function}</span>
                <span className="flex items-center gap-1"><Clock size={14} /> {job.type}</span>
              </div>
            </div>

            {/* Description */}
            {job.description && (
              <div className="mb-8">
                <div className="font-body text-mmg-silver-chalice leading-relaxed whitespace-pre-line">{job.description}</div>
              </div>
            )}

            {/* Responsibilities */}
            {job.responsibilities && (
              <div className="mb-8">
                <h2 className="font-heading font-bold text-lg text-white mb-4">{t.jobs.whatYoullDo}</h2>
                <div className="font-body text-mmg-silver-chalice leading-relaxed whitespace-pre-line">{job.responsibilities}</div>
              </div>
            )}

            {/* Requirements */}
            {job.requirements && (
              <div className="mb-10">
                <h2 className="font-heading font-bold text-lg text-white mb-4">{t.jobs.whatYouNeed}</h2>
                <div className="font-body text-mmg-silver-chalice leading-relaxed whitespace-pre-line">{job.requirements}</div>
              </div>
            )}

            {/* Apply CTA */}
            <div className="border-t border-mmg-mine-shaft pt-10">
              {!showForm ? (
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-3 border border-white bg-white text-black px-8 py-4 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
                  data-testid="apply-btn"
                >
                  <Send size={18} /> {t.jobs.applyForJob}
                </button>
              ) : (
                <div className="border border-mmg-mine-shaft p-6 md:p-8">
                  <h2 className="font-heading font-bold text-xl text-white mb-2">{t.jobs.applyForJob}</h2>
                  <p className="text-xs text-mmg-silver-chalice mb-6">* {t.jobs.requiredField}</p>
                  <form onSubmit={handleApply} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-mmg-silver-chalice mb-1">{t.jobs.firstName} *</label>
                        <input type="text" required value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="apply-first-name" />
                      </div>
                      <div>
                        <label className="block text-sm text-mmg-silver-chalice mb-1">{t.jobs.lastName} *</label>
                        <input type="text" required value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="apply-last-name" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-mmg-silver-chalice mb-1">{t.jobs.emailField} *</label>
                        <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="apply-email" />
                      </div>
                      <div>
                        <label className="block text-sm text-mmg-silver-chalice mb-1">{t.jobs.phone}</label>
                        <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="apply-phone" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-mmg-silver-chalice mb-1">{t.jobs.resumeUrl}</label>
                      <input type="url" value={formData.resume_url} onChange={(e) => setFormData({...formData, resume_url: e.target.value})} placeholder="https://linkedin.com/in/..." className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body" data-testid="apply-resume" />
                    </div>
                    <div>
                      <label className="block text-sm text-mmg-silver-chalice mb-1">{t.jobs.coverLetter}</label>
                      <textarea value={formData.cover_letter} onChange={(e) => setFormData({...formData, cover_letter: e.target.value})} rows={6} className="w-full py-3 px-4 bg-black border border-mmg-mine-shaft text-white font-body resize-none" data-testid="apply-cover" />
                    </div>
                    
                    {/* CAPTCHA */}
                    <div className="flex items-center gap-4 py-2">
                      <span className="text-mmg-silver-chalice font-body text-sm whitespace-nowrap">
                        {lang === "de" ? "Sicherheitsfrage" : "Security question"}: {captchaNum1} + {captchaNum2} =
                      </span>
                      <input
                        type="number"
                        value={captchaAnswer}
                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                        placeholder="?"
                        required
                        className="w-20 py-2 px-3 font-body text-base text-white bg-black border border-mmg-mine-shaft focus:border-white outline-none transition-colors text-center"
                        data-testid="apply-captcha-input"
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                      <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 disabled:opacity-50" data-testid="submit-application-btn">
                        <Send size={16} /> {submitting ? "..." : t.jobs.submit}
                      </button>
                      <button type="button" onClick={() => setShowForm(false)} className="px-6 py-3 border border-mmg-mine-shaft text-mmg-silver-chalice font-heading text-sm uppercase hover:text-white transition-colors">
                        <X size={16} className="inline mr-1" /> Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

// Site Content Context
const SiteContentContext = createContext();
const useSiteContent = () => useContext(SiteContentContext);

// Language Provider
const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [siteContent, setSiteContent] = useState(null);
  const [seoSettings, setSeoSettings] = useState(null);
  const t = translations[language];

  useEffect(() => {
    axios.get(`${API}/site-content`).then(res => setSiteContent(res.data)).catch(() => {});
    axios.get(`${API}/seo-settings`).then(res => {
      setSeoSettings(res.data);
      
      // Initialize Google Tag Manager if ID is provided
      if (res.data?.google_tag_manager_id) {
        const gtmId = res.data.google_tag_manager_id;
        
        // Add GTM script to head
        const gtmScript = document.createElement('script');
        gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
        document.head.appendChild(gtmScript);
        
        // Add GTM noscript to body
        const gtmNoscript = document.createElement('noscript');
        const gtmIframe = document.createElement('iframe');
        gtmIframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
        gtmIframe.height = '0';
        gtmIframe.width = '0';
        gtmIframe.style.display = 'none';
        gtmIframe.style.visibility = 'hidden';
        gtmNoscript.appendChild(gtmIframe);
        document.body.insertBefore(gtmNoscript, document.body.firstChild);
      }
    }).catch(() => {});
  }, []);

  // Update document title and meta tags when language or SEO settings change
  useEffect(() => {
    const updateSEO = async () => {
    if (seoSettings) {
      const title = seoSettings.site_title?.[language] || seoSettings.site_title?.en || 'Mahzod Media Group';
      const description = seoSettings.site_description?.[language] || seoSettings.site_description?.en || 'Digital Innovation, Creative Solutions, Global Reach';
      const keywords = seoSettings.keywords?.[language] || seoSettings.keywords?.en || '';
      
      document.title = title;
      
      // Update or create meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = description;
      
      // Update or create meta keywords
      let metaKeys = document.querySelector('meta[name="keywords"]');
      if (!metaKeys) {
        metaKeys = document.createElement('meta');
        metaKeys.name = 'keywords';
        document.head.appendChild(metaKeys);
      }
      metaKeys.content = keywords;
      
      // Add Open Graph meta tags for social sharing
      const ogTags = [
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: window.location.href },
        { property: 'og:image', content: seoSettings.og_image || `${window.location.origin}/logo.png` },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
      ];
      
      ogTags.forEach(tag => {
        let meta = document.querySelector(`meta[${tag.property ? 'property' : 'name'}="${tag.property || tag.name}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          if (tag.property) meta.setAttribute('property', tag.property);
          if (tag.name) meta.setAttribute('name', tag.name);
          document.head.appendChild(meta);
        }
        meta.content = tag.content;
      });
      
      // Add structured data (Schema.org JSON-LD) for AI/Search engines
      let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script');
        structuredDataScript.type = 'application/ld+json';
        document.head.appendChild(structuredDataScript);
      }
      
      // Get company info from API for Schema.org
      let companyInfo = {
        name: "Mahzod Media Group",
        ceo_name: "Mohab Hoffmann-Yassin",
        street: "",
        zip: "",
        city: "",
        country: "Deutschland",
        email: "info@mahzodmediagroup.com",
        phone: "",
        website: window.location.origin
      };
      
      try {
        const companyRes = await axios.get(`${API}/company-info/public`);
        if (companyRes.data) {
          companyInfo = { ...companyInfo, ...companyRes.data };
        }
      } catch (e) {
      }
      
      // Build comprehensive structured data for AI SEO - ALL 3 TYPES
      const structuredDataArray = [];
      
      // 1. Organization Schema
      const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": companyInfo.name,
        "url": window.location.origin,
        "logo": `${window.location.origin}/logo.png`,
        "description": description,
        "founder": {
          "@type": "Person",
          "name": companyInfo.ceo_name || "Mohab Hoffmann-Yassin",
          "jobTitle": "CEO & Founder"
        },
        "address": companyInfo.street ? {
          "@type": "PostalAddress",
          "streetAddress": companyInfo.street,
          "postalCode": companyInfo.zip,
          "addressLocality": companyInfo.city,
          "addressCountry": companyInfo.country || "DE"
        } : undefined,
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "Customer Service",
          "email": companyInfo.email,
          "telephone": companyInfo.phone || undefined
        },
        "sameAs": companyInfo.social_links && companyInfo.social_links.length > 0 
          ? companyInfo.social_links 
          : ["https://www.linkedin.com/company/mahzod-media-group", "https://www.instagram.com/mahzodmediagroup"]
      };
      structuredDataArray.push(organizationSchema);
      
      // 2. LocalBusiness Schema (for local SEO)
      const localBusinessSchema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": companyInfo.name,
        "image": `${window.location.origin}/logo.png`,
        "url": window.location.origin,
        "telephone": companyInfo.phone || undefined,
        "email": companyInfo.email,
        "address": companyInfo.street ? {
          "@type": "PostalAddress",
          "streetAddress": companyInfo.street,
          "postalCode": companyInfo.zip,
          "addressLocality": companyInfo.city,
          "addressRegion": "NRW",
          "addressCountry": "DE"
        } : undefined,
        "geo": companyInfo.city === "Mettingen" ? {
          "@type": "GeoCoordinates",
          "latitude": 52.3167,
          "longitude": 7.7833
        } : undefined,
        "priceRange": "€€",
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      };
      structuredDataArray.push(localBusinessSchema);
      
      // 3. ProfessionalService Schema (for service-based SEO)
      const professionalServiceSchema = {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": companyInfo.name,
        "description": "Digital Media Agency specializing in web development, branding, and digital marketing",
        "url": window.location.origin,
        "image": `${window.location.origin}/logo.png`,
        "telephone": companyInfo.phone || undefined,
        "email": companyInfo.email,
        "address": companyInfo.street ? {
          "@type": "PostalAddress",
          "streetAddress": companyInfo.street,
          "postalCode": companyInfo.zip,
          "addressLocality": companyInfo.city,
          "addressCountry": "DE"
        } : undefined,
        "areaServed": {
          "@type": "Country",
          "name": "Germany"
        },
        "serviceType": ["Web Development", "Digital Marketing", "Brand Design", "SEO"],
        "founder": {
          "@type": "Person",
          "name": companyInfo.ceo_name || "Mohab Hoffmann-Yassin"
        }
      };
      structuredDataArray.push(professionalServiceSchema);
      
      // Add author info if provided (for trust & authority)
      if (seoSettings.author_info) {
        organizationSchema.author = {
          "@type": "Person",
          "name": seoSettings.author_info.name || companyInfo.ceo_name || "Mahzod Media Group Team",
          "url": seoSettings.author_info.url || window.location.origin,
          "jobTitle": seoSettings.author_info.jobTitle || "CEO"
        };
      }
      
      // Add FAQ Schema for AI (direct answers to common questions)
      if (seoSettings.faq_schema && seoSettings.faq_schema.length > 0) {
        structuredDataArray.push({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": seoSettings.faq_schema.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        });
      }
      
      // Add WebPage schema with speakable content for voice search
      if (seoSettings.speakable_content) {
        structuredDataArray.push({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": title,
          "description": description,
          "url": window.location.href,
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [seoSettings.speakable_content.cssSelector || ".speakable", "h1", "h2"]
          }
        });
      }
      
      structuredDataScript.textContent = JSON.stringify(structuredDataArray);
      
      // Apple & iOS specific meta tags
      if (seoSettings.apple_touch_icon_url) {
        let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
        if (!appleTouchIcon) {
          appleTouchIcon = document.createElement('link');
          appleTouchIcon.rel = 'apple-touch-icon';
          document.head.appendChild(appleTouchIcon);
        }
        appleTouchIcon.href = `${process.env.REACT_APP_BACKEND_URL}${seoSettings.apple_touch_icon_url}`;
      }
      
      // Apple Web App Meta Tags
      const appleMetas = [
        { name: 'apple-mobile-web-app-capable', content: seoSettings.apple_mobile_web_app_capable ? 'yes' : 'no' },
        { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
        { name: 'apple-mobile-web-app-title', content: seoSettings.apple_mobile_web_app_title || title },
        { name: 'format-detection', content: 'telephone=no' }
      ];
      
      appleMetas.forEach(meta => {
        let metaTag = document.querySelector(`meta[name="${meta.name}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.name = meta.name;
          document.head.appendChild(metaTag);
        }
        metaTag.content = meta.content;
      });
      
      // Favicon
      if (seoSettings.favicon_url) {
        let favicon = document.querySelector('link[rel="icon"]');
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        favicon.href = `${process.env.REACT_APP_BACKEND_URL}${seoSettings.favicon_url}`;
      }
      
      // Update lang attribute on html element
      document.documentElement.lang = language;
    }
    };
    updateSEO();
  }, [language, seoSettings]);

  const langValue = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={langValue}>
      <SiteContentContext.Provider value={siteContent}>
        {children}
      </SiteContentContext.Provider>
    </LanguageContext.Provider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

// Section Redirect Component - Redirects /services, /portfolio, etc. to homepage with scroll
function SectionRedirect({ section }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Navigate to home page
    navigate('/', { replace: true });
    
    // After navigation, scroll to section
    setTimeout(() => {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [navigate, section]);
  
  return null;
}

// SEO Page Redirect - Redirects to the backend's static SEO page for crawlers
function SEORedirect() {
  useEffect(() => {
    // Redirect to the backend SEO page
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/../seo`;
  }, []);
  
  // Fallback content for crawlers that don't execute JavaScript
  return (
    <div className="min-h-screen bg-black text-white p-8">
      <h1 className="text-4xl font-bold text-center mb-8">Mahzod Media Group</h1>
      <p className="text-center text-lg mb-4">Digital Innovation. Creative Solutions. Global Reach.</p>
      <p className="text-center">
        <a href="/" className="text-blue-400 hover:underline">Zur Hauptseite</a>
      </p>
    </div>
  );
}

// 404 Not Found Page
function NotFoundPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  
  const texts = {
    de: { title: "Seite nicht gefunden", text: "Die angeforderte Seite existiert nicht.", back: "Zurück" },
    en: { title: "Page not found", text: "The requested page does not exist.", back: "Go back" },
    ar: { title: "الصفحة غير موجودة", text: "الصفحة المطلوبة غير موجودة.", back: "رجوع" }
  };
  const txt = texts[lang] || texts.en;
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <AlertTriangle size={64} className="text-mmg-silver-chalice mx-auto mb-6" />
        <h1 className="font-heading font-bold text-4xl text-white mb-4">404</h1>
        <h2 className="font-heading font-bold text-2xl text-white mb-4">{txt.title}</h2>
        <p className="font-body text-mmg-silver-chalice mb-8">{txt.text}</p>
        <button 
          onClick={() => navigate(-1)} 
          className="inline-flex items-center gap-2 border border-white bg-white text-black px-6 py-3 font-heading font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300"
          data-testid="notfound-back-btn"
        >
          <ArrowLeft size={18} /> {txt.back}
        </button>
      </div>
    </div>
  );
}

// Custom Page - Dynamic CMS page
const CustomPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/custom-pages/${slug}`);
        if (res.data && res.data.published) {
          setPage(res.data);
        } else {
          setNotFound(true);
        }
      } catch (e) {
        setNotFound(true);
      }
      setLoading(false);
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="custom-page-loading">
        <Navbar />
        <main className="pt-20 min-h-screen bg-black flex items-center justify-center">
          <p className="text-mmg-silver-chalice">Loading...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return <NotFoundPage />;
  }

  return (
    <div data-testid="custom-page">
      <Navbar />
      <main className="pt-20">
        <section className="py-24 md:py-32 bg-black min-h-screen">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <h1 className="font-heading font-semibold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white mb-12">
              {page.title?.[language] || page.title?.de || page.title?.en}
            </h1>

            <div 
              className="prose prose-invert max-w-none text-mmg-silver-chalice prose-headings:text-white prose-headings:font-heading prose-p:font-body prose-a:text-white prose-a:hover:opacity-80"
              dangerouslySetInnerHTML={{ __html: page.content?.[language] || page.content?.de || page.content?.en || '' }}
            />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

function AppContent() {
  const { t } = useLanguage();
  
  return (
    <div className="App bg-black min-h-screen" dir={t.dir}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/brand" element={<BrandPage />} />
          <Route path="/impressum" element={<ImprintPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/page/:slug" element={<CustomPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/jobs/:jobId" element={<JobDetailPage />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/*" element={<Portal />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* Section redirect routes - redirect to home with hash */}
          <Route path="/services" element={<SectionRedirect section="services" />} />
          <Route path="/leistungen" element={<SectionRedirect section="services" />} />
          <Route path="/portfolio" element={<SectionRedirect section="portfolio" />} />
          <Route path="/projekte" element={<SectionRedirect section="portfolio" />} />
          <Route path="/contact" element={<SectionRedirect section="contact" />} />
          <Route path="/kontakt" element={<SectionRedirect section="contact" />} />
          <Route path="/about" element={<SectionRedirect section="about" />} />
          <Route path="/ueber-uns" element={<SectionRedirect section="about" />} />
          {/* SEO page for crawlers - redirect to backend */}
          <Route path="/seo" element={<SEORedirect />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position={t.dir === 'rtl' ? 'bottom-left' : 'bottom-right'}
        theme="dark"
        toastOptions={{
          style: {
            background: '#141414',
            border: '1px solid #262626',
            color: '#FFFFFF',
          },
        }}
      />
    </div>
  );
}

export default App;
